import { useState } from 'react'
import { Package, Plus, X, Edit2 } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useParts, useAddPart, useUpdatePart, useDeletePart } from '../api/hooks'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

// ─── Add / Edit Part modal ────────────────────────────────────────────────────

function PartModal({ part, onClose }) {
  const addPart    = useAddPart()
  const updatePart = useUpdatePart()
  const isEdit     = !!part

  const [form, setForm] = useState({
    name:                part?.name                  || '',
    partNumber:          part?.part_number           || '',
    supplier:            part?.supplier              || '',
    description:         part?.description           || '',
    unitCost:            part?.unit_cost   != null   ? String(part.unit_cost)    : '0',
    stockQuantity:       part?.stock_quantity != null ? String(part.stock_quantity) : '0',
    vehicleRegistration: part?.vehicle_registration  || '',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    const data = {
      name:                form.name,
      partNumber:          form.partNumber          || null,
      supplier:            form.supplier            || null,
      description:         form.description         || null,
      unitCost:            parseFloat(form.unitCost)      || 0,
      stockQuantity:       parseInt(form.stockQuantity)   || 0,
      vehicleRegistration: form.vehicleRegistration.trim() || null,
    }
    try {
      if (isEdit) {
        await updatePart.mutateAsync({ id: part.id, ...data })
        toast.success('Part updated')
      } else {
        await addPart.mutateAsync(data)
        toast.success('Part added')
      }
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save part')
    }
  }

  const isPending = addPart.isPending || updatePart.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
            {isEdit ? 'Edit Part' : 'Add Part'}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Name</label>
            <input type="text" value={form.name} onChange={set('name')}
              placeholder="e.g. Oil filter" className={inputCls} required />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
              Vehicle registration
            </label>
            <input
              type="text"
              value={form.vehicleRegistration}
              onChange={set('vehicleRegistration')}
              placeholder="e.g. AB12 CDE — leave blank for general stock"
              className={inputCls}
            />
            <p className="mt-1 text-xs text-[var(--text-muted)]">
              Ties this part to a specific vehicle. Leave blank for garage-wide stock.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Part number</label>
              <input type="text" value={form.partNumber} onChange={set('partNumber')}
                placeholder="OEM-12345" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Supplier</label>
              <input type="text" value={form.supplier} onChange={set('supplier')}
                placeholder="GSF, Euro Car Parts…" className={inputCls} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Description</label>
            <textarea rows={2} value={form.description} onChange={set('description')}
              className={clsx(inputCls, 'resize-none')} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Unit cost (£)</label>
              <input type="number" min="0" step="0.01" value={form.unitCost} onChange={set('unitCost')}
                placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Stock quantity</label>
              <input type="number" min="0" step="1" value={form.stockQuantity} onChange={set('stockQuantity')}
                placeholder="0" className={inputCls} />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Part'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Part row ─────────────────────────────────────────────────────────────────

function PartRow({ part, canAdmin, onEdit }) {
  const deletePart = useDeletePart()

  const handleArchive = async () => {
    if (!window.confirm(`Archive "${part.name}"? It will no longer appear in new jobs.`)) return
    try {
      await deletePart.mutateAsync(part.id)
      toast.success('Part archived')
    } catch {
      toast.error('Archive failed')
    }
  }

  return (
    <div className="flex items-center gap-4 px-5 py-4 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <p className="text-sm font-medium text-[var(--text-primary)]">{part.name}</p>
          {part.vehicle_registration && (
            <span className="text-xs px-2 py-0.5 bg-indigo-500/15 text-indigo-400 rounded-full font-medium">
              {part.vehicle_registration}
            </span>
          )}
          {part.part_number && (
            <span className="text-xs text-[var(--text-muted)] font-mono">{part.part_number}</span>
          )}
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          {part.supplier && `${part.supplier} · `}
          {part.description || 'No description'}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">£{Number(part.unit_cost).toFixed(2)}</p>
        <p className={clsx('text-xs', part.stock_quantity > 0 ? 'text-green-400' : 'text-[var(--text-muted)]')}>
          {part.stock_quantity > 0 ? `${part.stock_quantity} in stock` : 'Out of stock'}
        </p>
      </div>
      {canAdmin && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 text-[var(--text-muted)] hover:text-indigo-400 rounded-lg transition-colors"
          >
            <Edit2 size={14} />
          </button>
          <button
            onClick={handleArchive}
            disabled={deletePart.isPending}
            className="p-1.5 text-[var(--text-muted)] hover:text-red-400 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PartsPage() {
  const { user } = useAuth()
  const [showAdd,  setShowAdd]  = useState(false)
  const [editPart, setEditPart] = useState(null)

  const canAdmin = ['garage_admin', 'platform_admin'].includes(user?.role)

  const { data: parts = [], isLoading } = useParts()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Parts</h1>
          {!isLoading && parts.length > 0 && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {parts.length} {parts.length === 1 ? 'part' : 'parts'} in catalogue
            </p>
          )}
        </div>
        {canAdmin && (
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} />
            Add Part
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : parts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center mb-4">
            <Package size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">
            No parts yet
          </h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            {canAdmin
              ? 'Build your parts catalogue to speed up job creation.'
              : "Your garage hasn't added any parts to the catalogue yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {parts.map(p => (
            <PartRow
              key={p.id}
              part={p}
              canAdmin={canAdmin}
              onEdit={() => setEditPart(p)}
            />
          ))}
        </div>
      )}

      {showAdd  && <PartModal onClose={() => setShowAdd(false)} />}
      {editPart && <PartModal part={editPart} onClose={() => setEditPart(null)} />}
    </div>
  )
}
